<div class="metrics-list-container" (click)="$event.stopPropagation()">
  <div class="tracker-name-container">
    <mat-icon svgIcon="tracker"></mat-icon>
    <span class="tracker-name">{{ subtask.tracker?.name || '' }}</span>
  </div>
  @for (metric of filteredMetrics; track metric._id; let metricIndex = $index) {
    <!-- ====================================== -->
    <!-- READ-ONLY METRICS SECTION             -->
    <!-- ====================================== -->
    @if (metric.readOnly) {
      <div class="metric-title-container mb-16 read-only-metric">
        <span class="metric-name">{{ metric?.name || '' }}</span>
        <span class="read-only-value"
          >current value:
          @if (metric.type === TrackerMetricTypes.Boolean) {
            {{ (metric.value | numberToBoolean) || 'no value' }}
          } @else if ((metric.value || metric.value === '0') && metric.value !== 'null') {
            {{ metric.value }}
            {{ metric.value ? metric.uom || '' : '' }}
          } @else {
            no value
          }
        </span>
      </div>
    } @else {
      <div class="metric">
        <!-- ====================================== -->
        <!-- BOOLEAN TYPE METRICS SECTION          -->
        <!-- ====================================== -->
        @if (metric.type === TrackerMetricTypes.Boolean) {
          <div class="metric-container boolean-container">
            <span class="boolean-metric-name">{{ metric.name }}</span>
            @for (option of booleanButtonGroupOptions; track option; let i = $index) {
              <button
                class="boolean-button"
                (click)="onUpdateValue(option.value, metricIndex)"
                [class.selected-boolean-button]="form.at(metricIndex).get('value')?.value === option.value"
              >
                {{ option.display }}
              </button>
            }
          </div>
        }
        <!-- ====================================== -->
        <!-- NUMERIC TYPE METRICS SECTION          -->
        <!-- ====================================== -->
        @if (metric.type === TrackerMetricTypes.Numeric) {
          <!-- Basic Numeric -->
          @if (metric.numericType === MetricNumericType.Basic) {
            <div class="metric-container numeric-container basic-container" (click)="focusEditableValue($event)">
              <span class="metric-name">{{ metric.name }}</span>
              <div class="editable-value-wrapper">
                <span
                  class="editable-value"
                  [class.error-border]="metricValidationErrors()[metricIndex]"
                  role="textbox"
                  contenteditable
                  inputmode="numeric"
                  (input)="onContentEditableInput($event, metricIndex)"
                  (blur)="onContentEditableBlur($event, metricIndex)"
                  (keydown)="onContentEditableKeydown($event, metricIndex)"
                  #editableSpanNumeric
                ></span>
                @if (metricValidationErrors()[metricIndex]) {
                  <span class="validation-error-message">{{ metricValidationErrors()[metricIndex] }}</span>
                }
              </div>
              <div class="uom">{{ metric.uom || '' }}</div>
            </div>
          }
          <!-- Stepper Numeric -->
          @if (metric.numericType === MetricNumericType.Stepper) {
            <div class="metric-container numeric-container stepper-container">
              <span class="metric-name">{{ metric.name }}</span>

              <div class="stepper-controls">
                <button
                  class="stepper-button minus-button"
                  (click)="onStepperDecrease(metricIndex, $event)"
                  type="button"
                  [disabled]="
                    metric.min &&
                    (form.at(metricIndex).get('value')?.value
                      ? parseFloat(form.at(metricIndex).get('value')?.value)
                      : 0) <= parseFloat(metric.min)
                  "
                >
                  <mat-icon>remove</mat-icon>
                </button>

                <span class="stepper-value">{{ form.at(metricIndex).get('value')?.value || 0 }}</span>

                <button
                  class="stepper-button plus-button"
                  (click)="onStepperIncrease(metricIndex, $event)"
                  type="button"
                  [disabled]="
                    metric.max &&
                    (form.at(metricIndex).get('value')?.value
                      ? parseFloat(form.at(metricIndex).get('value')?.value)
                      : 0) >= parseFloat(metric.max)
                  "
                >
                  <mat-icon>add</mat-icon>
                </button>
              </div>

              <div class="uom">{{ metric.uom || '' }}</div>
            </div>
          }
          <!-- Range Numeric -->
          @if (metric.numericType === MetricNumericType.Range) {
            <div class="metric-container numeric-container range-container oph-slider-container">
              <div class="range-top-container">
                <span class="metric-name">{{ metric.name }}</span>
                <span class="value">{{ form.at(metricIndex).get('value')?.value || 0 }}</span>
                @if (metric.uom) {
                  <span class="uom">{{ metric.uom }}</span>
                }
              </div>
              <mat-slider [min]="parseFloat(metric.min) || 0" [max]="parseFloat(metric.max) || 100" [step]="1">
                <input
                  matSliderThumb
                  [value]="form.at(metricIndex).get('value')?.value || parseFloat(metric.defaultValue) || 0"
                  (input)="onSliderChange($event, metricIndex)"
                />
              </mat-slider>
              <div class="range-bottom-container">
                <span class="metric-min-max">{{ metric.min }}</span>
                <span class="metric-min-max">{{ metric.max }}</span>
              </div>
            </div>
          }
        }
        <!-- ====================================== -->
        <!-- SELECT TYPE METRICS SECTION           -->
        <!-- ====================================== -->
        @if (metric.type === TrackerMetricTypes.Select) {
          <div class="metric-container" [class.menu-open]="openMenuId === metric._id">
            <button
              [matMenuTriggerFor]="optionsMenu"
              class="select-button"
              (menuOpened)="onMenuOpened(metric._id)"
              (menuClosed)="onMenuClosed()"
            >
              <span class="metric-name">{{ metric.name || '' }} </span>
              <span class="select-value">{{ form.at(metricIndex).get('value')?.value || '' }}</span>
              <mat-icon svgIcon="chevron-down"></mat-icon>
            </button>
          </div>
          <mat-menu #optionsMenu="matMenu" xPosition="before">
            @for (option of metric.options; track option) {
              <button class="oph-select-menu-item" (click)="onOptionSelected(option?.option, metricIndex)">
                {{ option.option }}
              </button>
            }
          </mat-menu>
        }
        <!-- ====================================== -->
        <!-- TEXT TYPE METRICS SECTION             -->
        <!-- ====================================== -->
        @if (metric.type === TrackerMetricTypes.Text) {
          <div
            class="metric-container numeric-container basic-container"
            (click)="focusEditableValue($event, metricIndex)"
          >
            <span class="metric-name">{{ metric.name }}</span>
            @if (focusedTextMetricIndex() === metricIndex) {
              <input
                type="text"
                class="text-metric-input"
                [value]="form.at(metricIndex).get('value')?.value || ''"
                (input)="onTextInputChange($event, metricIndex)"
                (focus)="onTextInputFocus(metricIndex)"
                (blur)="onTextInputBlur(metricIndex)"
                #textInput
              />
            } @else {
              <span class="editable-value text-editable-value" role="textbox" #editableSpanText>{{
                form.at(metricIndex).get('value')?.value || ''
              }}</span>
            }
          </div>
        }
        <!-- ====================================== -->
        <!-- PHOTO TYPE METRICS SECTION            -->
        <!-- ====================================== -->
        @if (metric.type === TrackerMetricTypes.Photo) {
          <div class="metric-container photo-container">
            <span class="photo-metric-name">{{ metric.name }}</span>

            <current-sked-metric-photo-upload
              [currentValue]="form.at(metricIndex).get('value')?.value || ''"
              [metricName]="metric.name"
              (valueChange)="onUpdateValue($event, metricIndex)"
            >
            </current-sked-metric-photo-upload>
          </div>
        }
        <!-- ====================================== -->
        <!-- DATE TYPE METRICS SECTION             -->
        <!-- ====================================== -->
        @if (metric.type === TrackerMetricTypes.Date) {
          <!-- TODO: Implement date picker component -->
        }
        <!-- ====================================== -->
        <!-- DURATION TYPE METRICS SECTION         -->
        <!-- ====================================== -->
        @if (metric.type === TrackerMetricTypes.Duration) {
          <div class="metric-container duration-container">
            <span class="duration-metric-name">{{ metric.name }}</span>
            <oph-duration-input
              [control]="$any(form.at(metricIndex).get('value'))"
              [durationType]="$any(metric.durationType)"
            ></oph-duration-input>
          </div>
        }
      </div>
    }
  }
  <!-- ====================================== -->
  <!-- UPDATE BUTTON SECTION                 -->
  <!-- ====================================== -->
  @if ({formValid: formValid$ | async}; as state) {
    @if (showUpdateButton) {
      <div class="update-button-row">
        <div class="update-button-container" [matTooltip]="state.formValid && !subtaskNotAvailable ? '' : tooltipText">
          <current-sked-detail-update-button
            [loading]="loading"
            [subtaskIndex]="subtaskIndex"
            [disabled]="subtaskNotAvailable || state.formValid === false"
            [buttonText]="areAllMetricsReadOnly ? 'Complete Subtask' : 'Update & Complete'"
            (buttonClick)="onUpdateMetrics()"
          ></current-sked-detail-update-button>
        </div>
        @if (notInSked && taskExpired) {
          <button
            class="timestamp-button"
            (click)="openTimestampDialog()"
            *userHasRoleOption="rolePermissionOptions.Projects"
          >
            <mat-icon svgIcon="clock" [class.green-icon]="!!(timestamp$ | async)"></mat-icon>
          </button>
        }
      </div>
    }
  }
</div>
